import { call, put, select, takeLatest } from "redux-saga/effects";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { PinLegendsFilter } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";

import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import getPinLegendsFilter from "@app/util/map/getPinLegendsFilter";

import { filterCompanies, filterDeals, filterEntitiesWithLocation, filterPeople } from "./actions";

export function* onFilterCompanies({ payload }: ReturnType<typeof filterCompanies.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.COMPANY]);

    const response: ListResponse<Company> = yield callApi("fetchCompanies", orgId, {
      $filters: {
        $and: payload.requestFilters ?? [],
        $or: [
          {
            address: payload.query ? { $in: payload.query } : undefined,
            city: payload.query ? { $in: payload.query } : undefined,
            name: payload.query ? { $in: payload.query } : undefined,
            postalCode: payload.query ? { $in: payload.query } : undefined,
            region: payload.query ? { $in: payload.query } : undefined,
            regionCode: payload.query ? { $in: payload.query } : undefined,
          },
        ],
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        notAncestorOf: payload.isChildSelection ? payload.companyId : null,
        notDescendantOf: !payload.isChildSelection ? payload.companyId : null,
        pinLegends,
      },
      $limit: 100,
      $offset: 0,
      $order: payload.query ? "name" : "-updatedAt",
    });
    yield put(
      filterCompanies.success({
        companies: response.data,
        filtered: response.total,
        total: response.accessible,
      })
    );
  } catch (error) {
    yield put(filterCompanies.failure());
    yield put(handleError({ error }));
  }
}

export function* onFilterDeals({ payload }: ReturnType<typeof filterDeals.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.DEAL]);

    const response: ListResponse<Deal> = yield callApi("fetchDeals", orgId, {
      $filters: {
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        name: payload.query ? { $in: payload.query } : undefined,
        pinLegends,
      },
      $limit: 100,
      $offset: 0,
      $order: payload.query ? "name" : "-updatedAt",
    });

    yield put(
      filterDeals.success({
        deals: response.data,
        filtered: response.total,
        total: response.accessible,
      })
    );
  } catch (error) {
    yield put(filterDeals.failure());
    yield put(handleError({ error }));
  }
}

export function* onFilterPeople({ payload }: ReturnType<typeof filterPeople.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.PERSON]);

    const response: ListResponse<Person> = yield callApi("fetchPeople", orgId, {
      $filters: {
        $and: payload.requestFilters ?? [],
        $or: [
          {
            address: payload.query ? { $in: payload.query } : undefined,
            city: payload.query ? { $in: payload.query } : undefined,
            name: payload.query ? { $in: payload.query } : undefined,
            postalCode: payload.query ? { $in: payload.query } : undefined,
            region: payload.query ? { $in: payload.query } : undefined,
            regionCode: payload.query ? { $in: payload.query } : undefined,
          },
        ],
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        pinLegends,
      },
      $limit: 100,
      $offset: 0,
      $order: payload.query ? "name" : "-updatedAt",
    });

    yield put(
      filterPeople.success({
        filtered: response.total,
        people: response.data,
        total: response.accessible,
      })
    );
  } catch (error) {
    yield put(filterPeople.failure());
    yield put(handleError({ error }));
  }
}

export function* onFilterEntitiesWithLocation({
  payload,
}: ReturnType<typeof filterEntitiesWithLocation.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const companyPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [
      EntityType.COMPANY,
    ]);
    const personPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.PERSON]);

    const availableCompanies: ListResponse<Company> = yield callApi("fetchCompanies", orgId, {
      $filters: {
        $or: [
          {
            address: payload.query ? { $in: payload.query } : undefined,
            city: payload.query ? { $in: payload.query } : undefined,
            name: payload.query ? { $in: payload.query } : undefined,
            postalCode: payload.query ? { $in: payload.query } : undefined,
            region: payload.query ? { $in: payload.query } : undefined,
            regionCode: payload.query ? { $in: payload.query } : undefined,
          },
        ],
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        pinLegends: companyPinLegends,
      },
      $limit: 100,
      $offset: 0,
      $order: payload.query ? "name" : "-updatedAt",
    });

    const availablePersons: ListResponse<Person> = yield callApi("fetchPeople", orgId, {
      $filters: {
        $or: [
          {
            address: payload.query ? { $in: payload.query } : undefined,
            city: payload.query ? { $in: payload.query } : undefined,
            name: payload.query ? { $in: payload.query } : undefined,
            postalCode: payload.query ? { $in: payload.query } : undefined,
            region: payload.query ? { $in: payload.query } : undefined,
            regionCode: payload.query ? { $in: payload.query } : undefined,
          },
        ],
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        pinLegends: personPinLegends,
      },
      $limit: 100,
      $offset: 0,
      $order: payload.query ? "name" : "-updatedAt",
    });

    const result = [...(availableCompanies.data ?? []), ...(availablePersons.data ?? [])].sort(
      nameComparator
    );

    yield put(filterEntitiesWithLocation.success(result));
  } catch (error) {
    yield put(filterEntitiesWithLocation.failure());
    yield put(handleError({ error }));
  }
}

export function* entityAssociationsSaga() {
  yield takeLatest(filterCompanies.request, onFilterCompanies);
  yield takeLatest(filterDeals.request, onFilterDeals);
  yield takeLatest(filterPeople.request, onFilterPeople);
  yield takeLatest(filterEntitiesWithLocation.request, onFilterEntitiesWithLocation);
}
